import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { CONFIRM_JOB_ACTION, confirmJob } from '~/actions';
import { useAppSelector, useIsSuccess, useLoading, useToggle } from '~/hooks';
import type { IMultipleCounters } from '~/models/Job';

interface IUseConfirmInterview {
  professionalId: string;
  officeId: string;
}

export const useConfirmInterview = ({ professionalId, officeId }: IUseConfirmInterview) => {
  const dispatch = useDispatch();

  const selectedInterviewIdRef = useRef<string | null>(null);
  const [isOpen, controls] = useToggle();

  const allCandidates = useAppSelector((state) => state.job.allCandidates);

  const { isLoading } = useLoading([CONFIRM_JOB_ACTION]);
  const { clearSuccess } = useIsSuccess([CONFIRM_JOB_ACTION]);

  const handleConfirmInterview = (interviewId: string) => {
    selectedInterviewIdRef.current = interviewId;
    controls.setOn();
  };

  const handleConfirmCandidateInterview = (notes?: string) => {
    if (!selectedInterviewIdRef.current) return;

    const counter: IMultipleCounters = allCandidates?.find(
      (c: IMultipleCounters) => c.candidate.id === professionalId,
    );

    if (!counter) return;

    dispatch(
      confirmJob({
        jobId: selectedInterviewIdRef.current,
        data: { notes },
        counterOfferId: counter.id,
        candidate: {
          id: counter.candidate.id,
          full_name: counter.candidate.full_name,
          profile_image: counter.candidate.profile_image,
        },
        officeId,
      }),
    );
  };

  const handleCloseConfirmInterview = () => {
    selectedInterviewIdRef.current = null;
    controls.setOff();
    clearSuccess();
  };

  return {
    isConfirming: isLoading,
    isOpen,
    selectedInterviewId: selectedInterviewIdRef.current,
    handleCloseConfirmInterview,
    handleConfirmInterview,
    handleConfirmCandidateInterview,
  };
};
